import { useState } from "react";
import { useCallHistory, useCallHistoryCount, type CallHistoryFilters } from "@/hooks/use-calls";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 50;

function formatTime(ts: string | null): string {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const DATE_PRESETS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
  { label: "All", hours: 0 },
];

const DIRECTION_OPTIONS = [
  { label: "All", value: "" },
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
  { label: "Internal", value: "internal" },
];

export default function CallHistoryPage() {
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState("");
  const [datePreset, setDatePreset] = useState("All");
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  const filters: CallHistoryFilters = {};
  if (direction) filters.direction = direction;
  if (datePreset !== "All") {
    const preset = DATE_PRESETS.find((p) => p.label === datePreset);
    if (preset && preset.hours > 0) {
      filters.date_from = new Date(Date.now() - preset.hours * 3600 * 1000).toISOString();
    }
  }

  const skip = page * PAGE_SIZE;
  const { data: calls, isLoading } = useCallHistory(skip, PAGE_SIZE, filters);
  const { data: countData } = useCallHistoryCount(filters);
  const total = countData?.count ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Call History</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} calls recorded (last 7 days)</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">Direction:</label>
          <select
            value={direction}
            onChange={(e) => { setDirection(e.target.value); setPage(0); }}
            className="text-xs border border-border rounded-md px-2 py-1 bg-background"
          >
            {DIRECTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">Period:</label>
          <div className="flex gap-0.5">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => { setDatePreset(p.label); setPage(0); }}
                className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                  datePreset === p.label
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">Call ID</th>
              <th className="px-3 py-2 font-medium">Caller</th>
              <th className="px-3 py-2 font-medium">Connected</th>
              <th className="px-3 py-2 font-medium">Direction</th>
              <th className="px-3 py-2 font-medium">State</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              <th className="px-3 py-2 font-medium">Hangup Cause</th>
              <th className="px-3 py-2 font-medium w-8"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
            )}
            {!isLoading && (!calls || calls.length === 0) && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No call history</td></tr>
            )}
            {calls?.map((call) => {
              const isExpanded = expandedCallId === call.id;
              const hasHeaders = call.invite_headers && Object.keys(call.invite_headers).length > 0;
              return (
                <tr key={call.id} className={`border-b border-border ${isExpanded ? "bg-muted/20" : "hover:bg-muted/30"}`}>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatTime(call.start_time)}</td>
                  <td className="px-3 py-2 font-mono text-xs max-w-[120px] truncate" title={call.linked_id ?? ""}>
                    {call.linked_id || "-"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{call.caller_id_num || "-"}</div>
                    {call.caller_id_name && <div className="text-xs text-muted-foreground">{call.caller_id_name}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{call.connected_line_num || "-"}</div>
                    {call.connected_line_name && <div className="text-xs text-muted-foreground">{call.connected_line_name}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      call.direction === "inbound" ? "bg-blue-100 text-blue-800" :
                      call.direction === "outbound" ? "bg-purple-100 text-purple-800" :
                      call.direction === "internal" ? "bg-teal-100 text-teal-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {call.direction || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2">{call.dial_status || call.state || "-"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{formatDuration(call.duration_seconds)}</td>
                  <td className="px-3 py-2 text-xs">{call.hangup_cause_txt || call.hangup_cause || "-"}</td>
                  <td className="px-3 py-2">
                    {hasHeaders && (
                      <button
                        onClick={() => setExpandedCallId(isExpanded ? null : call.id)}
                        className="p-1 hover:bg-muted rounded"
                        title="Toggle invite headers"
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {expandedCallId && (() => {
        const call = calls?.find((c) => c.id === expandedCallId);
        if (!call?.invite_headers) return null;
        return (
          <div className="rounded-md border border-border bg-muted/20 p-4">
            <h3 className="text-sm font-medium mb-2">Invite Headers</h3>
            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(call.invite_headers, null, 2)}
            </pre>
          </div>
        );
      })()}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}